import { useMemo } from "react";
import { InsightsAggregateResponse, TruncationCause } from "../types";

interface UseChartTruncationOptions {
  response: InsightsAggregateResponse | null;
  loading: boolean;
}

interface UseChartTruncationResult {
  criteriaTruncated: boolean;
  totalTruncated: boolean;
  groupsTruncated: boolean;
  isTruncated: boolean;
  causes: TruncationCause[];
}

/**
 * Pure reporter over the aggregate response.
 * Returns nothing-truncated while loading or before the first response,
 * so the banner doesn't flicker between fetches.
 */
export function useChartTruncation({
  response,
  loading,
}: UseChartTruncationOptions): UseChartTruncationResult {
  return useMemo(() => {
    if (loading || !response) {
      return {
        criteriaTruncated: false,
        totalTruncated: false,
        groupsTruncated: false,
        isTruncated: false,
        causes: [],
      };
    }
    const criteriaTruncated = response.criteria_truncated;
    const totalTruncated = response.truncated;
    const groupsTruncated = response.truncated_groups;
    const causes: TruncationCause[] = [];
    if (criteriaTruncated) {
      causes.push({
        kind: "criteria",
        criteriaCount: response.criteria_count,
        cap: response.criteria_cap,
      });
    }
    if (groupsTruncated) {
      causes.push({
        kind: "groups",
        groupCount: response.group_count,
        cap: response.group_cap,
      });
    }
    if (totalTruncated) {
      causes.push({
        kind: "total",
        totalEvaluations: response.total_evaluations,
        cap: response.match_cap,
      });
    }
    return {
      criteriaTruncated,
      totalTruncated,
      groupsTruncated,
      isTruncated: criteriaTruncated || totalTruncated || groupsTruncated,
      causes,
    };
  }, [response, loading]);
}
